// Google Sheets 接続の正式 OAuth フロー（Slice 5c）。
// PWA 用。Chrome 拡張ではないため、Google 公式 GIS / Picker スクリプトをランタイムで読み込む。
// スコープは drive.file のみ。選択したファイルへの読み書きに限定し、トークンはメモリ保持だけにする。

const SCOPE = "https://www.googleapis.com/auth/drive.file";

type GoogleTokenResponse = { access_token?: string; error?: string };
type PickerResult = { spreadsheetId: string; name: string };

type DocsView = {
  setSelectFolderEnabled: (enabled: boolean) => DocsView;
  setMode: (mode: string) => DocsView;
};
type PickerBuilder = {
  addView: (view: unknown) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  setDeveloperKey: (key: string) => PickerBuilder;
  setCallback: (cb: (data: Record<string, unknown>) => void) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
};
type GooglePickerApi = {
  ViewId: { SPREADSHEETS: string };
  DocsViewMode: { LIST: string };
  Response: { ACTION: string; DOCUMENTS: string };
  Action: { PICKED: string; CANCEL: string };
  Document: { ID: string; NAME: string };
  DocsView: new (viewId: string) => DocsView;
  PickerBuilder: new () => PickerBuilder;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (opts: {
            client_id: string;
            scope: string;
            callback: (resp: GoogleTokenResponse) => void;
          }) => { requestAccessToken: () => void };
        };
      };
      picker?: GooglePickerApi;
    };
    gapi?: { load: (name: string, opts: { callback: () => void; onerror?: () => void }) => void };
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) { resolve(); return; }
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Google の認証スクリプトを読み込めませんでした: ${src}`));
    document.head.appendChild(el);
  });
}

async function ensureGsi(): Promise<void> {
  await loadScript("https://accounts.google.com/gsi/client");
  if (!window.google?.accounts?.oauth2) throw new Error("Google Identity Services を利用できません");
}

async function ensurePicker(): Promise<void> {
  await loadScript("https://apis.google.com/js/api.js");
  await new Promise<void>((resolve, reject) => {
    window.gapi?.load("picker", { callback: resolve, onerror: () => reject(new Error("Google Picker を利用できません")) });
  });
  if (!window.google?.picker) throw new Error("Google Picker を利用できません");
}

export async function getSheetsAccessToken(clientId: string): Promise<string> {
  if (!clientId) throw new Error("Google OAuth Client ID が未設定です");
  await ensureGsi();
  return new Promise((resolve, reject) => {
    const client = window.google!.accounts!.oauth2!.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: (resp) => {
        if (resp.error) reject(new Error(resp.error));
        else if (!resp.access_token) reject(new Error("Google アクセストークンを取得できませんでした"));
        else resolve(resp.access_token);
      },
    });
    client.requestAccessToken();
  });
}

export async function pickSpreadsheet(apiKey: string, token: string): Promise<PickerResult> {
  if (!apiKey) throw new Error("Google Picker API Key が未設定です");
  await ensurePicker();
  const pickerApi = window.google!.picker!;
  return new Promise((resolve, reject) => {
    const view = new pickerApi.DocsView(pickerApi.ViewId.SPREADSHEETS)
      .setSelectFolderEnabled(false)
      .setMode(pickerApi.DocsViewMode.LIST);
    const picker = new pickerApi.PickerBuilder()
      .addView(view)
      .setOAuthToken(token)
      .setDeveloperKey(apiKey)
      .setCallback((data) => {
        const action = data[pickerApi.Response.ACTION];
        if (action === pickerApi.Action.PICKED) {
          const docs = data[pickerApi.Response.DOCUMENTS] as Array<Record<string, string>> | undefined;
          const doc = docs?.[0];
          const spreadsheetId = doc?.[pickerApi.Document.ID];
          const name = doc?.[pickerApi.Document.NAME] ?? "Google スプレッドシート";
          if (!spreadsheetId) reject(new Error("選択したスプレッドシートの ID を取得できませんでした"));
          else resolve({ spreadsheetId, name });
        } else if (action === pickerApi.Action.CANCEL) {
          reject(new Error("シート選択がキャンセルされました"));
        }
      })
      .build();
    picker.setVisible(true);
  });
}

export async function connectGoogleSheet(opts: { clientId: string; apiKey?: string }): Promise<{ token: string; picked?: PickerResult }> {
  const token = await getSheetsAccessToken(opts.clientId);
  if (!opts.apiKey) return { token };
  const picked = await pickSpreadsheet(opts.apiKey, token);
  return { token, picked };
}
